import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import * as yup from 'yup';
import type { CreateClientFormData } from '@/types/clientsApiTypes';
import { nullableString } from '@/utils/validations';
import { CLIENT_PREFERENCES_QUERY_KEY } from '../../../consts/client-detail';
import { useUpdateClientPreferencesMutation } from '../../../hooks/useUpdateClientPreferencesMutation';
import { ModalKeys } from '../../EditSectionModals/types';
import { defineSection } from '../../EditSectionModals/utils/defineSection';
import { EditFlightPreferencesForm } from './EditFlightPreferencesForm';
import type { FlightPreferencesFormValues, FlightPreferencesPayload } from './types';

const schema = yup.object({
  fly_private_aviation: yup.string().oneOf(['yes', 'no']).nullable().default(null),
  preferred_private_aviation_company: nullableString().defined(),
  seat: yup.string().oneOf(['Window', 'Aisle']).nullable().optional(),
  class_preference: yup.array().of(yup.string().required()).required().default([]),
});

function useFlightPreferencesOnSave() {
  const mutation = useUpdateClientPreferencesMutation();
  const queryClient = useQueryClient();

  return async (payload: FlightPreferencesPayload) => {
    await mutation.mutateAsync(payload.preference_data, {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: [CLIENT_PREFERENCES_QUERY_KEY] });
        toast.success('Preferences updated successfully');
      },
    });
  };
}

function mapClientToValues(client: CreateClientFormData): FlightPreferencesFormValues {
  const fly = client?.primary_passenger?.preference_data?.fly;
  return {
    fly_private_aviation: fly?.fly_private_aviation ?? null,
    preferred_private_aviation_company: fly?.preferred_private_aviation_company ?? null,
    seat: fly?.seat ?? null,
    class_preference: (fly?.class_preference as string[] | undefined) ?? [],
  };
}

function mapValuesToPayload(values: yup.InferType<typeof schema>): FlightPreferencesPayload {
  return {
    preference_data: {
      fly: {
        fly_private_aviation: values.fly_private_aviation,
        preferred_private_aviation_company: values.preferred_private_aviation_company?.trim() || null,
        seat: values.seat ?? 'Window',
        class_preference: values.class_preference ?? [],
      },
    },
  };
}

export const flightPreferencesSection = defineSection<
  (typeof ModalKeys)['flight_preferences'],
  CreateClientFormData,
  typeof schema,
  FlightPreferencesPayload
>({
  key: ModalKeys.flight_preferences,
  title: 'Flight preferences',
  subtitle: 'Client preferences related to air travel and onboard experience.',
  schema,
  mapClientToValues,
  mapValuesToPayload,
  useOnSave: useFlightPreferencesOnSave,
  Fields: EditFlightPreferencesForm,
});